import type {TreeItem} from '@visma-swno/vsn-navigation';
import {APPROVAL_MENU_ITEMS, CONFIGURATION_URL_PART, OTHER_SETTINGS_URL_PART, SETTINGS_URL_PART} from 'utils/constants';
import {canViewConfiguration, isSettingsVisible} from 'utils/accessRights.function';

export const APPROVAL_MODULE_ID = 'ApprovalCenter';
export const APPROVAL_MODULE_LABEL = 'Approval';

export type TranslateFn = (key: string, ...args: unknown[]) => string;

export type UserRoles = {
    globalRoles?: {isApprover?: boolean};
    [key: string]: unknown;
};

type MenuConfigItem = {
    label: string;
    url: string;
    rightsToView?: string;
    featureFlag?: string;
    globalRoles?: {isApprover?: boolean};
    children?: MenuConfigItem[];
    detailsView?: {url?: string; uidUrl?: string};
};

export type MenuNode = {id: string; url?: string; items?: MenuNode[]};

/** Resolved LaunchDarkly flags by key, so this file stays pure and testable. */
export type FeatureFlags = Record<string, boolean | undefined>;

function userCanView(
    item: MenuConfigItem,
    userRoles: UserRoles | undefined,
    flags: FeatureFlags = {},
): boolean {
    // An item behind an unresolved or disabled flag is not in the menu at all.
    if (item.featureFlag && !flags[item.featureFlag]) return false;

    // Role gates that aren't expressible via `rightsToView`.
    if (item.url === OTHER_SETTINGS_URL_PART && !userRoles?.globalRoles?.isApprover) return false;
    if (item.url === CONFIGURATION_URL_PART && !canViewConfiguration(userRoles ?? {})) return false;
    if (item.url === SETTINGS_URL_PART && !isSettingsVisible(userRoles)) return false;

    if (!item.rightsToView) return true;
    if (userRoles?.[item.rightsToView]) return true;
    return Boolean(item.globalRoles?.isApprover && userRoles?.globalRoles?.isApprover);
}

// Every menu id (top-level item urls plus `parent + child` ids), unfiltered.
export const ALL_MENU_IDS: Set<string> = (() => {
    const ids = new Set<string>();
    for (const item of APPROVAL_MENU_ITEMS as MenuConfigItem[]) {
        ids.add(item.url);
        for (const child of item.children || []) {
            ids.add(item.url + child.url);
        }
    }
    return ids;
})();

// Map a detail-view path (e.g. `/task/123`) back to its owning menu id so
// breadcrumbs find a match.
export function resolveCurrentItem(pathname: string): string {
    for (const item of APPROVAL_MENU_ITEMS as MenuConfigItem[]) {
        for (const child of item.children || []) {
            const childId = item.url + child.url;
            if (pathname === childId || pathname.startsWith(childId + '/')) {
                return childId;
            }
        }
        const detailsPrefixes = [item.detailsView?.url, item.detailsView?.uidUrl].filter(Boolean) as string[];
        if (detailsPrefixes.some(prefix => pathname.startsWith(prefix))) {
            return item.url;
        }
    }
    return pathname;
}

export function buildModuleTree(
    translateFn: TranslateFn,
    userRoles: UserRoles | undefined,
    flags: FeatureFlags = {},
): TreeItem {
    return {
        id: APPROVAL_MODULE_ID,
        label: APPROVAL_MODULE_LABEL,
        items: (APPROVAL_MENU_ITEMS as MenuConfigItem[])
            .filter(item => userCanView(item, userRoles, flags))
            .map(item => {
                const children = (item.children || [])
                    .filter(child => userCanView(child, userRoles, flags))
                    .map(child => ({
                        id: item.url + child.url,
                        label: translateFn(child.label),
                        url: item.url + child.url,
                    }));
                // Parents are detected by `"items" in item`; omit the key on leaves.
                return children.length > 0
                    ? {id: item.url, label: translateFn(item.label), items: children}
                    : {id: item.url, label: translateFn(item.label), url: item.url};
            }),
    } as TreeItem;
}

// Only collect navigable leaves. Parent sections aren't pages — sitting on a parent URL
// renders its index child (e.g. /configuration → Substitutes), which can itself be filtered
// out by the user's role. Treating a parent as "visible" would let the user remain on a
// broken page; treating only leaves as visible makes the redirect fire in that case.
export function collectMenuTargets(nodes: MenuNode[]): {visibleLeafUrls: Set<string>; firstLeafUrl?: string} {
    const visibleLeafUrls = new Set<string>();
    let firstLeafUrl: string | undefined;
    const walk = (items: MenuNode[]) => {
        for (const node of items) {
            if (typeof node.url === 'string') {
                visibleLeafUrls.add(node.url);
                if (firstLeafUrl === undefined) firstLeafUrl = node.url;
            }
            if (Array.isArray(node.items)) {
                walk(node.items);
            }
        }
    };
    walk(nodes);
    return {visibleLeafUrls, firstLeafUrl};
}
